/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * TurnoutHeatmapView — precinct (rows) × election_date (columns) grid
 * of turnout percentages.
 *
 * Data source is the agreed v1 contract documented in
 * `memory-bank/apiContracts-analytics-v1.md`. The wrapper at
 * `src/api/analytics.ts` returns the in-memory mock fixture until
 * Agent A's backend endpoint lands; flip with VITE_USE_MOCK_ANALYTICS=0.
 *
 * Visual: a CSS-grid heatmap with one cell per (precinct, election),
 * colored on a gradient from cool slate (low turnout) to hot red
 * (high turnout). Hovering a cell shows the underlying voted_count
 * and pct via the native title tooltip; clicking a row drills into
 * the existing PrecinctView.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { fetchTurnoutHeatmap } from '@/api/analytics';
import type { HeatmapCell, TurnoutHeatmapResponse } from '@/types/analytics';
import { InfoTip } from '@/components/InfoTip';
import { VisualKey } from '@/components/VisualKey';
import { DeltaChip } from '@/components/DeltaChip';
import { BarMeter } from '@/components/BarMeter';
import { RankBadge } from '@/components/RankBadge';
import { OnboardingModal, OnboardingHelpButton } from '@/components/OnboardingModal';
import { ViewHeader } from '@/components/ViewHeader';
import { LiveBadge } from '@/components/LiveBadge';

// -------------------------------------------------------------------------
// Color scale
// -------------------------------------------------------------------------

/**
 * Map a 0..100 turnout percentage to a Tailwind-friendly background
 * color. Five buckets keep the palette legible without becoming a
 * gradient blur. Above 80% gets the brand red so the headline is
 * immediately legible.
 */
function bucketColor(pct: number): { bg: string; text: string } {
  if (pct >= 80) return { bg: 'bg-status-danger-bg', text: 'text-white' };
  if (pct >= 65) return { bg: 'bg-status-danger-bg/80', text: 'text-white' };
  if (pct >= 50) return { bg: 'bg-status-warning-bg/70', text: 'text-fg-primary' };
  if (pct >= 30) return { bg: 'bg-status-warning-bg/40', text: 'text-fg-primary' };
  if (pct > 0) return { bg: 'bg-bg-elevated/60', text: 'text-fg-secondary' };
  return { bg: 'bg-bg-raised/40', text: 'text-fg-muted' };
}

const LEGEND_BUCKETS = [
  { label: '0', color: 'bg-bg-raised/40' },
  { label: '<30', color: 'bg-bg-elevated/60' },
  { label: '30–49', color: 'bg-status-warning-bg/40' },
  { label: '50–64', color: 'bg-status-warning-bg/70' },
  { label: '65–79', color: 'bg-status-danger-bg/80' },
  { label: '80+', color: 'bg-status-danger-bg' },
];

// -------------------------------------------------------------------------
// Main view
// -------------------------------------------------------------------------

export function TurnoutHeatmapView({
  onBack,
  onDrill,
}: {
  onBack: () => void;
  onDrill?: (target: string, value: any) => void;
}) {
  const [data, setData] = useState<TurnoutHeatmapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);

  // Detect mock mode for the LiveBadge — same env var the wrapper reads.
  const usingMock = (() => {
    const raw = (import.meta as any).env?.VITE_USE_MOCK_ANALYTICS;
    if (raw === undefined || raw === null || raw === '') return true;
    return raw !== '0' && raw !== 'false';
  })();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTurnoutHeatmap({ sinceYear: 2014, topNPrecincts: 50 })
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          const status = e?.response?.status;
          setError(
            status === 404
              ? 'Heatmap endpoint not found. Backend may not be deployed yet — flip VITE_USE_MOCK_ANALYTICS=1 in .env to view fixtures.'
              : status
              ? `Backend error ${status}`
              : 'Could not reach the analytics backend.',
          );
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onboardingNode = (
    <OnboardingModal
      viewKey="turnoutheatmap"
      title="Turnout Heatmap"
      intro="Each row is a precinct, each column is a past election. The cell color and number show what percent of that precinct's currently-registered voters actually cast a ballot in that election. Hot red = high turnout; faded slate = no-shows."
      termIds={['turnout_heatmap', 'roster_turnout', 'precinct', 'sparkline']}
      workflows={[
        'Spot precincts that turn out for general but skip primaries (rows that flip cool→hot every other column).',
        'Identify off-cycle no-show precincts and target them for early outreach.',
        'Click any precinct row to drill into its profile.',
      ]}
      forceOpen={forceOnboard}
      onClose={() => setForceOnboard(false)}
    />
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {onboardingNode}
        <Header onBack={onBack} usingMock={usingMock} onHelp={() => setForceOnboard(true)} />
        <div className="bg-bg-raised rounded-xl p-12 border border-border-base text-center text-fg-muted">
          Loading turnout heatmap…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {onboardingNode}
        <Header onBack={onBack} usingMock={usingMock} onHelp={() => setForceOnboard(true)} />
        <div className="bg-status-danger-bg/30 rounded-xl p-6 border border-status-danger text-status-danger">
          {error}
        </div>
      </div>
    );
  }

  if (!data || data.precincts.length === 0 || data.elections.length === 0) {
    return (
      <div className="space-y-6">
        {onboardingNode}
        <Header onBack={onBack} usingMock={usingMock} onHelp={() => setForceOnboard(true)} />
        <div className="bg-bg-raised rounded-xl p-12 border border-border-base text-center text-fg-muted">
          No election data yet for this tenant.
        </div>
      </div>
    );
  }

  return (
    <>
      {onboardingNode}
      <HeatmapBody
        data={data}
        usingMock={usingMock}
        onBack={onBack}
        onDrill={onDrill}
        onHelp={() => setForceOnboard(true)}
      />
    </>
  );
}

// Split into a sub-component so the cell lookup memoization can hook
// off `data` cleanly. The sparse flat-cells shape from the backend is
// pivoted into a `${precinct_num}|${election_date}` map once per
// response, then read back as O(1) lookups during render.
function HeatmapBody({
  data,
  usingMock,
  onBack,
  onDrill,
  onHelp,
}: {
  data: TurnoutHeatmapResponse;
  usingMock: boolean;
  onBack: () => void;
  onDrill?: (target: string, value: any) => void;
  onHelp?: () => void;
}) {
  const cellMap = useMemo(() => {
    const m = new Map<string, HeatmapCell>();
    for (const c of data.cells) {
      m.set(`${c.precinct_num}|${c.election_date}`, c);
    }
    return m;
  }, [data]);

  // Per-row average turnout (across all elections this row has cells for).
  // We drop empty cells so rows with sparse election history don't get
  // dragged toward 0 unfairly.
  const rowAvgTurnout = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of data.precincts) {
      const pcts: number[] = [];
      for (const e of data.elections) {
        const cell = cellMap.get(`${row.precinct_num}|${e.election_date}`);
        if (cell) pcts.push(cell.turnout_pct);
      }
      const avg = pcts.length
        ? pcts.reduce((s, v) => s + v, 0) / pcts.length
        : 0;
      m.set(row.precinct_num, avg);
    }
    return m;
  }, [data, cellMap]);

  // Grid-wide mean turnout — used as the baseline for DeltaChips and the
  // BarMeter tick. Computed from the row averages so it's stable even when
  // a single election is missing for a few precincts.
  const meanTurnout = useMemo(() => {
    const values = Array.from(rowAvgTurnout.values()).filter((v) => v > 0);
    if (values.length === 0) return 0;
    return values.reduce((s, v) => s + v, 0) / values.length;
  }, [rowAvgTurnout]);

  // Ranked-by-avg order for RankBadge lookups (1 = best turnout).
  const rowRank = useMemo(() => {
    const m = new Map<string, number>();
    const sorted = [...data.precincts].sort(
      (a, b) =>
        (rowAvgTurnout.get(b.precinct_num) ?? 0) -
        (rowAvgTurnout.get(a.precinct_num) ?? 0),
    );
    sorted.forEach((row, idx) => m.set(row.precinct_num, idx + 1));
    return m;
  }, [data, rowAvgTurnout]);

  return (
    <div className="space-y-6">
      <Header onBack={onBack} usingMock={usingMock} onHelp={onHelp} />
      <VisualKey />

      <div className="bg-bg-raised rounded-xl p-4 sm:p-6 border border-border-base">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
              Turnout by Precinct × Election <InfoTip termId="turnout_heatmap" />
            </h2>
            <p className="text-xs text-fg-muted mt-1">
              {data.precincts.length} precincts × {data.elections.length}{' '}
              elections {data.election_type_filter ? `(${data.election_type_filter} only)` : '(all types)'}.
              Click a precinct to drill into its profile.
            </p>
          </div>
          <Legend />
        </div>

        {/* Wide tables flex past the page edge — page-level horizontal scroll */}
        <div>
          <table className="w-full text-xs border-separate border-spacing-1">
            <thead className="sticky top-[var(--app-header-h)] z-10">
              <tr>
                <th className="text-left text-fg-muted font-semibold sticky left-0 top-[var(--app-header-h)] bg-bg-raised z-20 px-2 py-2">
                  Precinct
                </th>
                <th className="text-right text-fg-muted font-semibold bg-bg-raised px-2 py-2">
                  Total
                </th>
                <th className="text-left text-fg-muted font-semibold bg-bg-raised px-2 py-2 whitespace-nowrap">
                  Avg Turnout
                </th>
                <th className="text-center text-fg-muted font-semibold bg-bg-raised px-2 py-2">
                  Rank
                </th>
                {data.elections.map((e) => (
                  <th
                    key={e.election_date}
                    className="text-center text-fg-muted font-semibold bg-bg-raised px-1 py-2 whitespace-nowrap"
                    title={`${e.label} — ${e.election_date}`}
                  >
                    <div>{e.label}</div>
                    <div className="text-[10px] text-fg-muted">
                      {e.election_type}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.precincts.map((row) => {
                const avg = rowAvgTurnout.get(row.precinct_num) ?? 0;
                const rnk = rowRank.get(row.precinct_num) ?? 0;
                return (
                <tr key={row.precinct_num}>
                  <td className="text-left sticky left-0 bg-bg-raised z-10 px-2 py-1">
                    <button
                      onClick={() => onDrill?.('precinct', row.precinct_num)}
                      className="text-fg-primary hover:text-accent-fg text-left"
                      title={row.precinct_name}
                    >
                      <div className="font-bold">P{row.precinct_num}</div>
                      <div className="text-[10px] text-fg-muted">
                        {row.precinct_name}
                      </div>
                    </button>
                  </td>
                  <td className="text-right text-fg-secondary tabular-nums px-2 py-1">
                    {row.total_voters.toLocaleString()}
                  </td>
                  <td className="px-2 py-1 min-w-[150px]">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-fg-primary tabular-nums font-semibold">
                        {avg.toFixed(1)}%
                      </span>
                      <DeltaChip
                        value={avg}
                        baseline={meanTurnout}
                        label="vs mean"
                        format="pct"
                        compact
                      />
                    </div>
                    <BarMeter
                      value={avg}
                      baseline={meanTurnout}
                      tierColor={false}
                      height={3}
                    />
                  </td>
                  <td className="text-center px-2 py-1">
                    <RankBadge rank={rnk} total={data.precincts.length} />
                  </td>
                  {data.elections.map((e) => {
                    const cell = cellMap.get(`${row.precinct_num}|${e.election_date}`);
                    const pct = cell?.turnout_pct ?? 0;
                    const c = bucketColor(pct);
                    return (
                      <td key={e.election_date} className="px-0 py-0">
                        <div
                          className={`${c.bg} ${c.text} text-center px-2 py-1 rounded-sm font-bold tabular-nums`}
                          title={
                            cell
                              ? `${e.label} (${e.election_date})\n${cell.voter_count.toLocaleString()} voted\n${pct.toFixed(1)}% turnout`
                              : `${e.label} (${e.election_date})\nNo ballots recorded`
                          }
                        >
                          {cell ? pct.toFixed(0) : '·'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-[11px] text-fg-muted">
          Numbers in cells are turnout percentages (voted / registered ×
          100). Empty cells (·) mean no ballots from that precinct in
          that election.
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

function Header({
  onBack,
  usingMock,
  onHelp,
}: {
  onBack: () => void;
  usingMock: boolean;
  onHelp?: () => void;
}) {
  return (
    <ViewHeader
      title="Turnout Heatmap"
      tagline="Precinct × election turnout rates pulled from voter_elections."
      tone="success"
      inline={
        <>
          <LiveBadge mock={usingMock} />
          <InfoTip termId="turnout_heatmap" />
        </>
      }
      actions={onHelp ? <OnboardingHelpButton onClick={onHelp} /> : undefined}
    />
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-1 text-[10px] text-fg-muted">
      <span className="mr-1">Turnout %</span>
      {LEGEND_BUCKETS.map((b) => (
        <div key={b.label} className="flex items-center gap-1">
          <span className={`inline-block w-4 h-3 rounded-sm ${b.color}`} />
          <span className="mr-1">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

export default TurnoutHeatmapView;
